import { kgFromLevelDelta, getSiloDimensionsFromCurves } from './siloLevelToKg';
import { filterStableSiloProfiles } from './siloProfileStabilityFilter';
import {
  DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT,
  isSignificantLevelVariation,
} from './siloLevelVariationFilter';

const EMPTY_LEVEL_THRESHOLD = 0.1;
/** Weight deltas below 50 kg are sensor/rounding noise, not an entrada or saída. */
const MIN_SIGNIFICANT_WEIGHT_DELTA_KG = 50;

/** Same empty threshold used by SiloCard display metrics. */
export function normalizeSiloLevel(level) {
  const v = Number(level);
  if (!Number.isFinite(v) || v < EMPTY_LEVEL_THRESHOLD) return 0;
  return v;
}

function getRawLevel(profile) {
  if (profile?.level == null) return null;
  const v = Number(profile.level);
  return Number.isFinite(v) ? v : null;
}

function getEffectiveLevel(profile) {
  const raw = getRawLevel(profile);
  if (raw == null) return null;
  return normalizeSiloLevel(raw);
}

function getEffectiveWeight(profile) {
  const level = getEffectiveLevel(profile);
  if (level == null || level <= 0) return 0;
  const weight = profile?.weight != null ? Number(profile.weight) : null;
  if (weight == null || !Number.isFinite(weight) || weight < 0) return null;
  return weight;
}

function getDensityForProfile(profile, foods, defaultDensity) {
  const foodId = profile?.food;
  if (foodId != null && Array.isArray(foods) && foods.length > 0) {
    const food = foods.find((f) => String(f.pk) === String(foodId));
    const density = Number(food?.density);
    if (Number.isFinite(density) && density > 0) return density;
  }

  const weight = Number(profile?.weight);
  const volume = Number(profile?.volume);
  if (Number.isFinite(weight) && Number.isFinite(volume) && volume > 0 && weight > 0) {
    return weight / volume;
  }

  return Number(defaultDensity) || 750;
}

function resolveIntervalDensity(sorted, foods, defaultDensity) {
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const density = getDensityForProfile(sorted[i], foods, defaultDensity);
    if (Number.isFinite(density) && density > 0) return density;
  }
  return Number(defaultDensity) || 750;
}

function kgFromLevelDeltaSafe(deltaLevelPercent, dims, densityKgM3) {
  if (!dims || !Number.isFinite(deltaLevelPercent) || deltaLevelPercent <= 0) return 0;
  return kgFromLevelDelta(deltaLevelPercent, dims, densityKgM3);
}

function computeNetMetrics(sorted, dims, density, minSignificantDeltaPercent, applyOutlierFilter) {
  const empty = {
    netEntradasLevel: 0,
    netSaidasLevel: 0,
    netEntradasKgReal: 0,
    netSaidasKgReal: 0,
  };

  if (sorted.length < 2) return empty;

  const firstWeight = getEffectiveWeight(sorted[0]);
  const lastWeight = getEffectiveWeight(sorted[sorted.length - 1]);
  const hasWeightPair = firstWeight != null && lastWeight != null;

  let netEntradasLevel = 0;
  let netSaidasLevel = 0;
  let netEntradasKgReal = 0;
  let netSaidasKgReal = 0;

  const firstLevel = getEffectiveLevel(sorted[0]);
  const lastLevel = getEffectiveLevel(sorted[sorted.length - 1]);
  const netLevel = firstLevel != null && lastLevel != null ? lastLevel - firstLevel : 0;
  const levelIsSignificant = firstLevel == null || lastLevel == null
    ? true
    : !applyOutlierFilter || isSignificantLevelVariation(netLevel, minSignificantDeltaPercent);

  if (levelIsSignificant && firstLevel != null && lastLevel != null) {
    if (netLevel > 0) netEntradasLevel = netLevel;
    else if (netLevel < 0) netSaidasLevel = Math.abs(netLevel);
  }

  if (levelIsSignificant && hasWeightPair) {
    const deltaWeight = lastWeight - firstWeight;
    if (deltaWeight >= MIN_SIGNIFICANT_WEIGHT_DELTA_KG) netEntradasKgReal = deltaWeight;
    else if (deltaWeight <= -MIN_SIGNIFICANT_WEIGHT_DELTA_KG) netSaidasKgReal = Math.abs(deltaWeight);
  } else if (levelIsSignificant && dims) {
    netEntradasKgReal = kgFromLevelDeltaSafe(netEntradasLevel, dims, density);
    netSaidasKgReal = kgFromLevelDeltaSafe(netSaidasLevel, dims, density);
  }

  return {
    netEntradasLevel,
    netSaidasLevel,
    netEntradasKgReal,
    netSaidasKgReal,
  };
}

function computeGrossLevelMetrics(sorted, applyOutlierFilter, minSignificantDeltaPercent) {
  let grossSubidaLevel = 0;
  let grossDescidaLevel = 0;

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const level0 = getEffectiveLevel(sorted[i]);
    const level1 = getEffectiveLevel(sorted[i + 1]);
    if (level0 == null || level1 == null) continue;

    const deltaLevel = level1 - level0;
    if (deltaLevel === 0) continue;

    if (applyOutlierFilter && !isSignificantLevelVariation(deltaLevel, minSignificantDeltaPercent)) {
      continue;
    }

    if (deltaLevel > 0) grossSubidaLevel += deltaLevel;
    else grossDescidaLevel += Math.abs(deltaLevel);
  }

  return { grossSubidaLevel, grossDescidaLevel };
}

/**
 * Build consolidated entradas/saídas per interval for a date range.
 * Prefers measured weight deltas when available; falls back to level geometry.
 * @param {Object} profilesBySilo - { [siloId]: Array<{ datetime_fetched, level?, weight?, food?, silo_name? }> }
 * @param {string} startDate - 'YYYY-MM-DD' or ISO
 * @param {string} endDate - 'YYYY-MM-DD' or ISO
 * @param {Object} options
 * @returns {{ intervals8h: Array, siloOrder: Array, intervalsPerSilo: Array }}
 */
export function buildConsolidatedDataForRange(profilesBySilo, startDate, endDate, options = {}) {
  const intervalHours = options.intervalHours != null ? options.intervalHours : 8;
  const minSignificantDeltaPercent = options.minSignificantDeltaPercent != null
    ? options.minSignificantDeltaPercent
    : DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT;
  const applyOutlierFilter = options.applyOutlierFilter !== false;
  const applyStabilityFilter = options.applyStabilityFilter !== false;
  const stabilityThreshold = options.stabilityThreshold != null ? options.stabilityThreshold : 5;
  const siloCurvesBySiloId = options.siloCurvesBySiloId || {};
  const defaultDensity = options.defaultDensity != null ? Number(options.defaultDensity) : 750;
  const foods = Array.isArray(options.foods) ? options.foods : [];

  const [sy, sm, sd] = startDate.substring(0, 10).split('-').map(Number);
  const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
  const [ey, em, ed] = endDate.substring(0, 10).split('-').map(Number);
  const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);

  const siloOrder = Object.keys(profilesBySilo).map((id) => {
    const firstValid = (profilesBySilo[id] || []).find((p) => p.silo_name);
    return {
      id,
      name: (firstValid && firstValid.silo_name) || `Silo ${id}`,
    };
  });

  const siloDimensionsBySiloId = {};
  siloOrder.forEach(({ id }) => {
    const curves = siloCurvesBySiloId[id] || siloCurvesBySiloId[String(id)];
    if (curves) {
      const dims = getSiloDimensionsFromCurves(curves);
      if (dims) siloDimensionsBySiloId[id] = dims;
    }
  });

  const intervals8h = [];
  const siloIntervalsMap = {};
  siloOrder.forEach(({ id }) => {
    siloIntervalsMap[id] = [];
  });

  const cleanProfilesBySilo = {};
  Object.keys(profilesBySilo).forEach((siloId) => {
    let profiles = (profilesBySilo[siloId] || []).filter((p) => getRawLevel(p) != null);
    if (applyStabilityFilter) {
      profiles = filterStableSiloProfiles(profiles, stabilityThreshold);
    }
    cleanProfilesBySilo[siloId] = profiles;
  });

  let intervalStart = new Date(start.getTime());
  while (intervalStart <= end) {
    let intervalEnd = new Date(intervalStart.getTime() + intervalHours * 60 * 60 * 1000);
    if (intervalEnd > end) {
      intervalEnd = new Date(end.getTime() + 1);
    }
    if (intervalStart.getTime() >= intervalEnd.getTime()) break;

    let totalGrossEntradasLevel = 0;
    let totalGrossSaidasLevel = 0;
    let totalNetEntradasLevel = 0;
    let totalNetSaidasLevel = 0;
    let totalNetEntradasKgReal = 0;
    let totalNetSaidasKgReal = 0;

    siloOrder.forEach(({ id: siloId }) => {
      const profiles = cleanProfilesBySilo[siloId] || [];
      const filtered = profiles.filter((p) => {
        const t = new Date(p.datetime_fetched);
        return t >= intervalStart && t < intervalEnd;
      });
      const sorted = [...filtered].sort(
        (a, b) => new Date(a.datetime_fetched) - new Date(b.datetime_fetched)
      );

      const { grossSubidaLevel, grossDescidaLevel } = computeGrossLevelMetrics(
        sorted,
        applyOutlierFilter,
        minSignificantDeltaPercent
      );

      const dims = siloDimensionsBySiloId[siloId];
      const density = resolveIntervalDensity(sorted, foods, defaultDensity);
      const {
        netEntradasLevel,
        netSaidasLevel,
        netEntradasKgReal,
        netSaidasKgReal,
      } = computeNetMetrics(
        sorted,
        dims,
        density,
        minSignificantDeltaPercent,
        applyOutlierFilter
      );

      totalGrossEntradasLevel += grossSubidaLevel;
      totalGrossSaidasLevel += grossDescidaLevel;
      totalNetEntradasLevel += netEntradasLevel;
      totalNetSaidasLevel += netSaidasLevel;
      totalNetEntradasKgReal += netEntradasKgReal;
      totalNetSaidasKgReal += netSaidasKgReal;

      siloIntervalsMap[siloId].push({
        start: intervalStart.toISOString(),
        end: intervalEnd.toISOString(),
        entradasKg: netEntradasLevel,
        saidasKg: netSaidasLevel,
        entradasLevel: netEntradasLevel,
        saidasLevel: netSaidasLevel,
        entradasKgReal: netEntradasKgReal,
        saidasKgReal: netSaidasKgReal,
        grossEntradasLevel: grossSubidaLevel,
        grossSaidasLevel: grossDescidaLevel,
        grossEntradasKgReal: 0,
        grossSaidasKgReal: 0,
        netEntradasLevel,
        netSaidasLevel,
        netEntradasKgReal: netEntradasKgReal,
        netSaidasKgReal: netSaidasKgReal,
      });
    });

    intervals8h.push({
      start: intervalStart.toISOString(),
      end: intervalEnd.toISOString(),
      entradasKg: totalNetEntradasLevel,
      saidasKg: totalNetSaidasLevel,
      entradasLevel: totalNetEntradasLevel,
      saidasLevel: totalNetSaidasLevel,
      entradasKgReal: totalNetEntradasKgReal,
      saidasKgReal: totalNetSaidasKgReal,
      grossEntradasLevel: totalGrossEntradasLevel,
      grossSaidasLevel: totalGrossSaidasLevel,
      grossEntradasKgReal: 0,
      grossSaidasKgReal: 0,
      netEntradasLevel: totalNetEntradasLevel,
      netSaidasLevel: totalNetSaidasLevel,
      netEntradasKgReal: totalNetEntradasKgReal,
      netSaidasKgReal: totalNetSaidasKgReal,
    });

    intervalStart = new Date(intervalEnd.getTime());
  }

  const intervalsPerSilo = siloOrder.map(({ id }) => ({
    siloId: id,
    intervals: siloIntervalsMap[id] || [],
  }));

  return {
    intervals8h,
    siloOrder,
    intervalsPerSilo,
    dailySummaries: intervals8h,
  };
}

/**
 * Add sacas (bags) to each interval using weight per bag.
 * @param {Object} rangeData - output of buildConsolidatedDataForRange
 * @param {number} sacaWeight - kg per bag
 * @returns {Object} rangeData with entradasSacas, saidasSacas on each interval
 */
export function withSacas(rangeData, sacaWeight) {
  if (!rangeData || !sacaWeight || sacaWeight <= 0) return rangeData;
  const weight = Number(sacaWeight) || 60;

  const addSacas = (interval) => {
    const entradasFonteKg = interval.entradasKgReal != null ? interval.entradasKgReal : interval.entradasKg;
    const saidasFonteKg = interval.saidasKgReal != null ? interval.saidasKgReal : interval.saidasKg;
    return {
      ...interval,
      entradasSacas: (Number(entradasFonteKg) || 0) / weight,
      saidasSacas: (Number(saidasFonteKg) || 0) / weight,
    };
  };

  return {
    ...rangeData,
    intervals8h: (rangeData.intervals8h || []).map(addSacas),
    intervalsPerSilo: (rangeData.intervalsPerSilo || []).map((s) => ({
      ...s,
      intervals: (s.intervals || []).map(addSacas),
    })),
    dailySummaries: (rangeData.dailySummaries || rangeData.intervals8h || []).map(addSacas),
  };
}
